import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate  } from 'react-router-dom'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Input,
  FormFeedback,
  Badge,
  Alert,
} from 'reactstrap';

import { useNewLeadValidation } from './formik';
import { getClients } from '../../../../redux/actions/client';
import {
  getLeadItems,
  createLead,
  getLeads,
} from '../../../../redux/actions/lead';
import { SOURCE_OPTIONS } from '../../constants';
import styles from './styles.module.scss';
import { currencyFormat } from '../../../../utils/utils';

function NewLeadForm({ onSubmit, onCancel }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clientState = useSelector((state) => state.client.get);
  const holdingState = useSelector((state) => state.lead.items);
  const createLeadState = useSelector((state) => state.lead.create);
  const [modal, setModal] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [holdingSearch, setHoldingSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [showHoldingSuggestions, setShowHoldingSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [useCompanyData, setUseCompanyData] = useState(false);

  const formik = useNewLeadValidation((values) => {
    setLoading(true);
    setErrorMessage('');

    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    const formData = {
      clientId: values.clientId,
      holdingId: values.holdingId,
      source: values.source,
      useCompanyData: values.useCompanyData,
    };

    // Add companyInfo if useCompanyData is true and client is a company
    if (values.useCompanyData && selectedClient?.data?.isCompany) {
      formData.companyInfo = selectedClient.data.companyInfo;
    }

    console.log('Creating lead with data:', formData);

    // Si es vendedor, agregar información de asignación automática
    if (role === 'vendedor') {
      formData.assignedTo = userEmail;
      formData.state = 'assigned'; // Ir directamente al estado asignado
    }

    dispatch(createLead(formData));
  });

  const toggle = (operation) => {
    setModal(!modal);
    if (operation === 'submit') {
      onSubmit();
    } else {
      onCancel();
    }
  };

  const handleClientSearch = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setShowClientSuggestions(true);

    if (value.length > 0) {
      dispatch(getClients(value));
    } else {
      setClients([]);
    }
  };

  const handleHoldingSearch = (e) => {
    const value = e.target.value;
    setHoldingSearch(value);
    setShowHoldingSuggestions(true);

    if (value.length > 0) {
      dispatch(getLeadItems(value));
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setUseCompanyData(false); // Reset company data selection
    formik.setFieldValue('clientId', client.id);
    formik.setFieldValue('useCompanyData', false);
    setShowClientSuggestions(false);
  };

  const handleHoldingSelect = (holding) => {
    setSelectedHolding(holding);
    setHoldingSearch('');
    formik.setFieldValue('holdingId', holding.id);
    setShowHoldingSuggestions(false);
  };

  const handleRemoveClient = () => {
    setSelectedClient(null);
    setUseCompanyData(false);
    formik.setFieldValue('clientId', null);
  };

  const handleCompanyDataToggle = () => {
    if (!selectedClient) return;

    const newValue = !useCompanyData;
    setUseCompanyData(newValue);
    formik.setFieldValue('useCompanyData', newValue);
  };

  const handleRemoveHolding = () => {
    setSelectedHolding(null);
    formik.setFieldValue('holdingId', null);
  };

  const handleCreateClient = () => {
    navigate('/client-form', { state: { returnTo: '/lead-form' } });
  };

  useEffect(() => {
    if (clientState?.data) {
      setClients(clientState.data);
    }
  }, [clientState]);

  useEffect(() => {
    if (createLeadState) {
      setLoading(false);
      if (createLeadState.status === 'success') {
        toggle('submit');
        dispatch(getLeads());
      } else if (createLeadState.status === 'danger') {
        setErrorMessage(
          createLeadState.message || 'Ha ocurrido un error al crear el lead',
        );
      }
    }
  }, [createLeadState]);

  return (
    <Modal isOpen={modal} toggle={toggle}>
      <ModalHeader toggle={toggle}>Nuevo lead</ModalHeader>
      <Form onSubmit={formik.handleSubmit} className={styles.form}>
        <ModalBody>
          {errorMessage && (
            <Alert color="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}

          <section className="mb-3">
            <Label for="clientId">Cliente</Label>
            <section className="position-relative">
              {selectedClient ? (
                <section>
                  <section className="d-flex align-items-center mb-2">
                    <Badge color="primary" className="mr-2 p-2">
                      {useCompanyData && selectedClient.data.isCompany
                        ? selectedClient.data.companyInfo?.name
                        : selectedClient.data.firstName +
                          ' ' +
                          selectedClient.data.lastName}{' '}
                      -{' '}
                      {useCompanyData && selectedClient.data.isCompany
                        ? selectedClient.data.companyInfo?.rut
                        : selectedClient.data.rut}
                    </Badge>
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      onClick={handleRemoveClient}
                    >
                      <i className="fa fa-times"></i>
                    </Button>
                  </section>
                  {selectedClient.data.isCompany && (
                    <section className="mb-2 ml-4">
                      <Label check className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={useCompanyData}
                          onChange={handleCompanyDataToggle}
                          className="mr-2"
                        />
                        Desea lead por empresa
                      </Label>
                    </section>
                  )}
                </section>
              ) : (
                <Input
                  id="clientId"
                  type="text"
                  placeholder="Buscar cliente por nombre, apellido, RUT, teléfono o correo electrónico"
                  value={clientSearch}
                  onChange={handleClientSearch}
                  onBlur={() =>
                    setTimeout(() => setShowClientSuggestions(false), 200)
                  }
                  valid={formik.touched.clientId && !formik.errors.clientId}
                  invalid={formik.touched.clientId && !!formik.errors.clientId}
                />
              )}
              {showClientSuggestions && (
                <section
                  className="position-absolute w-100 bg-white border rounded-bottom"
                  style={{ zIndex: 1000 }}
                >
                  {clients.length > 0 ? (
                    clients.map((client) => (
                      <section
                        key={client.id}
                        className="px-3 py-2 cursor-pointer hover-bg-light"
                        onClick={() => handleClientSelect(client)}
                      >
                        <span className="font-weight-bold">
                          {client.data.firstName} {client.data.lastName}
                        </span>
                        {client.data.isCompany && (
                          <span className="text-info ml-2">
                            <i className="fa fa-building mr-1"></i>
                            Empresa: {client.data.companyInfo?.name}
                          </span>
                        )}
                        <br />
                        <span className="text-muted">
                          {client.data.rut} | {client.data.phone}
                        </span>
                      </section>
                    ))
                  ) : (
                    <section className="px-3 py-2">
                      <p className="mb-0">No se encontraron clientes</p>
                      <Button
                        color="primary"
                        size="sm"
                        className="mt-2"
                        onClick={handleCreateClient}
                      >
                        <i className="fa fa-plus mr-2"></i>
                        Crear nuevo cliente
                      </Button>
                    </section>
                  )}
                </section>
              )}
            </section>
            <FormFeedback>{formik.errors.clientId}</FormFeedback>
          </section>

          <section className="mb-3">
            <Label for="holdingId">Bien</Label>
            <section className="position-relative">
              {selectedHolding ? (
                <section className="d-flex align-items-center">
                  <Badge color="primary" className="mr-2 p-2">
                    {selectedHolding.id} | {selectedHolding.brand} |{' '}
                    {selectedHolding.model} | {selectedHolding.year} |{' '}
                    {currencyFormat(selectedHolding.price)}
                  </Badge>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={handleRemoveHolding}
                  >
                    <i className="fa fa-times"></i>
                  </Button>
                </section>
              ) : (
                <Input
                  id="holdingId"
                  type="text"
                  placeholder="Buscar bien por número interno"
                  value={holdingSearch}
                  onChange={handleHoldingSearch}
                  onBlur={() =>
                    setTimeout(() => setShowHoldingSuggestions(false), 200)
                  }
                  valid={formik.touched.holdingId && !formik.errors.holdingId}
                  invalid={
                    formik.touched.holdingId && !!formik.errors.holdingId
                  }
                />
              )}
              {showHoldingSuggestions && holdingState?.data && (
                <section
                  className="position-absolute w-100 bg-white border rounded-bottom"
                  style={{ zIndex: 1000 }}
                >
                  {Array.isArray(holdingState.data) ? (
                    holdingState.data.map((holding) => (
                      <section
                        key={holding.id}
                        className="px-3 py-2 cursor-pointer hover-bg-light"
                        onClick={() => handleHoldingSelect(holding)}
                      >
                        <span className="font-weight-bold">{holding.id}</span>
                        <br />
                        <span className="text-muted">
                          {holding.brand} | {holding.model} | {holding.year} |{' '}
                          {currencyFormat(holding.price)}
                        </span>
                      </section>
                    ))
                  ) : (
                    <section
                      className="px-3 py-2 cursor-pointer hover-bg-light"
                      onClick={() => handleHoldingSelect(holdingState.data)}
                    >
                      <span className="font-weight-bold">
                        {holdingState.data.id}
                      </span>
                      <br />
                      <span className="text-muted">
                        {holdingState.data.brand} | {holdingState.data.model} |{' '}
                        {holdingState.data.year} |{' '}
                        {currencyFormat(holdingState.data.price)}
                      </span>
                    </section>
                  )}
                </section>
              )}
            </section>
            <FormFeedback>{formik.errors.holdingId}</FormFeedback>
          </section>

          <section className="mb-3">
            <Label for="source">Fuente</Label>
            <Input
              id="source"
              type="select"
              name="source"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.source}
              valid={formik.touched.source && !formik.errors.source}
              invalid={formik.touched.source && !!formik.errors.source}
            >
              <option value="">Selecciona una fuente</option>
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            <FormFeedback>{formik.errors.source}</FormFeedback>
          </section>
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            color="primary"
            disabled={!formik.isValid || !formik.dirty || loading}
          >
            {loading ? 'Creando...' : 'Crear lead'}
          </Button>{' '}
          <Button color="secondary" onClick={() => toggle('cancel')}>
            Cancelar
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default NewLeadForm;
